import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { Clock, Star, Film, RefreshCw, ChevronLeft, ChevronRight, Loader } from 'lucide-react';

const FeedDesktop = ({ user }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [ratings, setRatings] = useState({});
  const [votedEpisodes, setVotedEpisodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [currentImageDimensions, setCurrentImageDimensions] = useState({ width: 16, height: 9 });
  const navigate = useNavigate();
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Navigation functions with rating submission
  const nextEpisode = async () => {
    const currentEpisode = episodes[currentEpisodeIndex];
    if (!currentEpisode) return;
    
    // If not voted yet, submit rating before moving to next
    if (!votedEpisodes.includes(currentEpisode.id)) {
      await handleSubmitRating(currentEpisode.id);
    }
    
    // Move to next episode
    if (currentEpisodeIndex < episodes.length - 1) {
      setCurrentEpisodeIndex(currentEpisodeIndex + 1);
      setCurrentSceneIndex(0);
    }
  };
  
  const prevEpisode = () => {
    if (currentEpisodeIndex > 0) {
      setCurrentEpisodeIndex(currentEpisodeIndex - 1);
      setCurrentSceneIndex(0);
    }
  };
  
  // Image tap handler to cycle through scenes
  const handleImageTap = () => {
    if (!episodes[currentEpisodeIndex]) return;
    
    const episode = episodes[currentEpisodeIndex];
    if (episode && episode.episodeScenes && episode.episodeScenes.length > 0) {
      // Cycle through scenes (including cover image)
      const newSceneIndex = (currentSceneIndex + 1) % (episode.episodeScenes.length + 1);
      setCurrentSceneIndex(newSceneIndex);
      
      // Set image dimensions based on whether it's the cover or a scene
      if (newSceneIndex === 0) {
        // Cover image - use 3:2 aspect ratio
        setCurrentImageDimensions({ width: 3, height: 2 });
      } else {
        // For scene images, use their natural dimensions
        setCurrentImageDimensions({ width: 4, height: 3 }); // Default if we can't determine
      }
    }
  };
  
  const fetchFeed = async () => {
    if (!user || !user.uid) {
      console.log('No user found, skipping feed fetch');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Starting feed fetch for user:', user.uid);
      setLoading(true);
      setError(null);
      
      // Get user's friends
      const friendsRef = collection(db, 'users', user.uid, 'friends');
      const friendsSnapshot = await getDocs(friendsRef);
      const friendIds = friendsSnapshot.docs.map(doc => doc.data().friendUserId);
      
      console.log(`Found ${friendIds.length} friends`);
      
      // If no friends, show empty state
      if (friendIds.length === 0) {
        console.log('No friends found, showing empty state');
        setEpisodes([]);
        setLoading(false);
        return;
      }
      
      // Calculate timestamp for 72 hours ago
      const threeDaysAgo = new Date();
      threeDaysAgo.setHours(threeDaysAgo.getHours() - 72);
      const timestamp = Timestamp.fromDate(threeDaysAgo);
      
      console.log('Looking for episodes since:', threeDaysAgo.toISOString());
      
      // Collect all episodes from all friends' shows
      let allEpisodes = [];
      
      // For each friend, get their shows
      for (const friendId of friendIds) {
        try {
          console.log(`Fetching shows for friend: ${friendId}`);
          const friendShowsRef = collection(db, 'users', friendId, 'shows');
          const friendShowsSnapshot = await getDocs(friendShowsRef);
          
          console.log(`Found ${friendShowsSnapshot.docs.length} shows for friend ${friendId}`);
          
          // For each show, get recent episodes
          for (const showDoc of friendShowsSnapshot.docs) {
            try {
              const showId = showDoc.id;
              console.log(`Processing show: ${showId}`);
              
              // Get episodes created in the last 72 hours
              const episodesRef = collection(db, 'shows', showId, 'episodes');
              const episodesQuery = query(
                episodesRef,
                where('episodeCreationDate', '>=', timestamp)
              );
              
              const episodesSnapshot = await getDocs(episodesQuery);
              console.log(`Found ${episodesSnapshot.docs.length} recent episodes for show ${showId}`);
              
              // Process each episode
              for (const episodeDoc of episodesSnapshot.docs) {
                try {
                  const episodeData = episodeDoc.data();
                  console.log(`Processing episode: ${episodeDoc.id}`);
                  
                  // Check if user has already rated this episode
                  const hasUserRated = episodeData.episodeRatings && 
                                      episodeData.episodeRatings[user.uid];
                  
                  // Get show details
                  const showDetails = await getDoc(doc(db, 'shows', showId));
                  const showData = showDetails.exists() ? showDetails.data() : {};
                  
                  // Get creator details
                  let creatorData = { name: 'Unknown Creator', handle: 'unknown' };
                  if (episodeData.episodeCreator) {
                    const creatorDetails = await getDoc(doc(db, 'users', episodeData.episodeCreator));
                    if (creatorDetails.exists()) {
                      creatorData = creatorDetails.data();
                    }
                  }
                  
                  // Add episode to appropriate list
                  const enrichedEpisode = {
                    id: episodeDoc.id,
                    ...episodeData,
                    showName: showData.showName || 'Unknown Show',
                    showId: showId,
                    creatorName: creatorData.name || 'Unknown Creator',
                    creatorHandle: creatorData.handle || 'unknown',
                    hasVoted: hasUserRated
                  };
                  
                  allEpisodes.push(enrichedEpisode);
                } catch (episodeError) {
                  console.error(`Error processing episode ${episodeDoc.id}:`, episodeError);
                  // Continue with next episode
                }
              }
            } catch (showError) {
              console.error(`Error processing show ${showDoc.id}:`, showError);
              // Continue with next show
            }
          }
        } catch (friendError) {
          console.error(`Error processing friend ${friendId}:`, friendError);
          // Continue with next friend
        }
      }
      
      console.log(`Total episodes found: ${allEpisodes.length}`);
      
      // Sort episodes by creation date (newest first)
      allEpisodes.sort((a, b) => {
        const dateA = a.episodeCreationDate?.toDate() || new Date(0);
        const dateB = b.episodeCreationDate?.toDate() || new Date(0);
        return dateB - dateA;
      });
      
      // Separate unvoted episodes to show first
      const unvotedEpisodes = allEpisodes.filter(ep => !ep.hasVoted);
      const votedEpisodes = allEpisodes.filter(ep => ep.hasVoted);
      
      console.log(`Unvoted episodes: ${unvotedEpisodes.length}, Voted episodes: ${votedEpisodes.length}`);
      
      // Set state
      const combinedEpisodes = [...unvotedEpisodes, ...votedEpisodes];
      setEpisodes(combinedEpisodes);
      
      // Initialize ratings object with existing ratings or default to 5
      const initialRatings = {};
      allEpisodes.forEach(episode => {
        if (episode.hasVoted && episode.episodeRatings && episode.episodeRatings[user.uid]) {
          initialRatings[episode.id] = episode.episodeRatings[user.uid].rating;
        } else {
          initialRatings[episode.id] = 5; // Default rating of 5
        }
      });
      setRatings(initialRatings);
      
      console.log('Feed fetch completed successfully');
    } catch (err) {
      console.error('Error fetching feed:', err);
      setError('Failed to load feed. Please try again.');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchFeed();
  }, [user]);
  
  const handleRatingChange = (episodeId, value) => {
    setRatings(prev => ({
      ...prev,
      [episodeId]: value
    }));
    
    // If this is the first time rating, add to votedEpisodes
    if (!votedEpisodes.includes(episodeId)) {
      setVotedEpisodes(prev => [...prev, episodeId]);
    }
  };
  
  const handleSubmitRating = async (episodeId) => {
    if (!user || !episodeId || submitting) return;
    
    try {
      setSubmitting(true);
      
      const episode = episodes.find(ep => ep.id === episodeId);
      if (!episode) {
        throw new Error('Episode not found');
      }
      
      const rating = ratings[episodeId] || 5;
      
      // Get the episode document
      const episodeRef = doc(db, 'shows', episode.showId, 'episodes', episodeId);
      
      // Update the episode ratings
      await updateDoc(episodeRef, {
        [`episodeRatings.${user.uid}`]: {
          rating,
          timestamp: new Date()
        }
      });
      
      // Mark this episode as voted
      setVotedEpisodes(prev => [...prev, episodeId]);
      
      console.log(`Rating submitted: ${rating}/10 for episode ${episodeId}`);
      
    } catch (err) {
      console.error('Error submitting rating:', err);
      alert('Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Add this useEffect to create the loader style
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Custom loader animation */
 .loader {
  position: relative;
  width: 100px;
  height: 100px;
}

.loader:before , .loader:after{
  content: '';
  border-radius: 50%;
  position: absolute;
  inset: 0;
  box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.3) inset;
}
.loader:after {
  box-shadow: 0 2px 0 #6600cc inset;
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  0% {  transform: rotate(0)}
  100% { transform: rotate(360deg)}
}
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        backgroundColor: 'rgba(18, 18, 18, 0.8)',
        borderRadius: '0.5rem'
      }}>
        <span className="loader"></span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <p style={{ color: '#ff6b6b', marginBottom: '1rem' }}>{error}</p>
        <button
          onClick={fetchFeed}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#6600CC',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.9rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }
  
  if (episodes.length === 0) {
    return (
      <div style={{ 
        padding: '2rem',
        backgroundColor: '#1a1a1a',
        borderRadius: '0.5rem',
        textAlign: 'center',
        background: `
          radial-gradient(circle at top left, rgba(102, 0, 204, 0.4) 0%, rgba(0, 0, 0, 0) 30%),
          radial-gradient(circle at bottom right, rgba(102, 0, 204, 0.3) 0%, rgba(0, 0, 0, 0) 40%),
          radial-gradient(circle at top right, rgba(119, 0, 238, 0.2) 0%, rgba(0, 0, 0, 0) 35%),
          linear-gradient(135deg, rgba(26, 26, 26, 1) 0%, rgba(18, 18, 18, 1) 100%)
        `,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 2px rgba(102, 0, 204, 0.3) inset'
      }}>
        <p>No recent episodes from your friends.</p>
        <button
          onClick={() => navigate('/add-friend')}
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#6600CC',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.9rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Refresh Feed
        </button>
      </div>
    );
  }
  
  // If there are episodes to display, show the rating UI
  if (episodes.length > 0) {
    const currentEpisode = episodes[currentEpisodeIndex];
    
    // Calculate current image to display
    let currentImage = currentEpisode.episodeImage || 'https://via.placeholder.com/600x400?text=No+Image';
    if (currentSceneIndex > 0 && currentEpisode.episodeScenes && currentEpisode.episodeScenes.length > 0) {
      currentImage = currentEpisode.episodeScenes[currentSceneIndex - 1] || currentImage;
    }
    
    // Calculate progress percentage for progress bar
    const totalScenes = (currentEpisode.episodeScenes?.length || 0) + 1; // +1 for cover image
    const progress = ((currentSceneIndex + 1) / totalScenes) * 100;
    
    const currentRating = ratings[currentEpisode.id] || 5;
    const isVoted = votedEpisodes.includes(currentEpisode.id);
    
    return (
      <div style={{ 
        backgroundColor: '#1a1a1a', 
        borderRadius: '0.5rem',
        overflow: 'hidden',
        padding: '1.5rem',
        background: `
          radial-gradient(circle at top left, rgba(102, 0, 204, 0.4) 0%, rgba(0, 0, 0, 0) 30%),
          radial-gradient(circle at bottom right, rgba(102, 0, 204, 0.3) 0%, rgba(0, 0, 0, 0) 40%),
          radial-gradient(circle at top right, rgba(119, 0, 238, 0.2) 0%, rgba(0, 0, 0, 0) 35%),
          linear-gradient(135deg, rgba(26, 26, 26, 1) 0%, rgba(18, 18, 18, 1) 100%)
        `,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 2px rgba(102, 0, 204, 0.3) inset',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          opacity: 0.05,
          background: 'linear-gradient(45deg, transparent 65%, #6600CC 100%)',
          animation: 'gradientShift 8s ease infinite',
          borderRadius: '0.5rem',
        }}/>
        
        <style>{`
          @keyframes gradientShift {
            0% { opacity: 0.03; transform: scale(1); }
            50% { opacity: 0.08; transform: scale(1.02); }
            100% { opacity: 0.03; transform: scale(1); }
          }
        `}</style>
        
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          position: 'relative',
          zIndex: 1
        }}>
          <h3 style={{ margin: 0, fontWeight: '600', position: 'absolute', transform: 'translatex(-50%)', left: '50%' , color: 'grey'}}>Rate:      {currentEpisode.showName}</h3>
          <button
            onClick={fetchFeed}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '2rem',  }}>
          <button
            onClick={prevEpisode}
            disabled={currentEpisodeIndex === 0}
            style={{
              padding: '0.5rem',
              backgroundColor: 'transparent',
              color: currentEpisodeIndex === 0 ? '#555' : 'white',
              border: `none`,
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              cursor: currentEpisodeIndex === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            
              height: 'auto',
              alignSelf: 'stretch',
              flexShrink: 0
            }}
          >
            <ChevronLeft size={48} />
          </button>

          <div style={{display: 'flex', width: '70%', marginLeft: 'auto', marginRight: 'auto'}}>
          {/* Left side - Episode image */}
          <div style={{ flex: '1' }}>
            <div 
              onClick={handleImageTap}
              style={{
                position: 'relative',
                width: '375px', // Full width
                height: '250px', // Fixed height
                borderRadius: '0.5rem',
                overflow: 'hidden',
                marginTop: '1rem',
                marginBottom: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center', // Center the image horizontally
                alignItems: 'center', // Center the image vertically
                backgroundColor: '#111', // Dark background for letterboxing
                padding: 0 // Remove padding
              }}
            >
              {currentSceneIndex === 0 ? (
                // Cover image with 3:2 aspect ratio
                <div style={{
                  height: '250px', // Fixed height for 3:2 aspect ratio
                  width: '375px', // Fixed width for 3:2 aspect ratio (3:2 = 300:200)
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: '#000', // Black background
                  margin: '0 auto' // Center horizontally
                }}>
                  <img 
                    src={currentImage} 
                    alt={currentEpisode.episodeTitle}
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover' // Cover to fill the container
                    }}
                  />
                </div>
              ) : (
                // Scene images with natural proportions
                <img 
                  src={currentImage} 
                  alt={currentEpisode.episodeTitle}
                  style={{
                    height: '300px', // Fixed height to match cover image
                    width: 'auto', // Let width adjust based on aspect ratio
                    maxWidth: '100%', // Ensure it doesn't overflow the container
                    objectFit: 'contain' // Maintain aspect ratio
                  }}
                />
              )}
            </div>
            
            {/* Progress bar */}
            <div style={{
              height: '4px',
              backgroundColor: '#333',
              borderRadius: '2px',
              marginBottom: '1rem',
              overflow: 'hidden',
              width: '375px', // Full width
            }}>
              <div style={{
                height: '100%',
                width: `${progress}%`,
                backgroundColor: '#6600CC',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
          
          {/* Right side - Episode details and rating */}
          <div style={{ flex: '1', position: 'relative', marginLeft: '2rem' , 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)', borderRadius: '1rem',
                  backdropFilter:'blur(15px)', padding: '0rem 2rem'}}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', marginTop: '1.5rem' }}>
              <h3 style={{
                margin: '0',
                fontSize: '1.25rem',
                fontWeight: 'bold'
              }}>
                {currentEpisode.episodeTitle}
              </h3>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{
                  fontSize: '.9rem',
                  color: '#ccc',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  
                  {currentEpisode.episodeDuration}m <span style={{marginLeft: '2rem'}}>

                  {currentEpisode.episodeDate?.toDate().toLocaleDateString() || 
                   currentEpisode.episodeCreationDate?.toDate().toLocaleDateString()}</span>
                </div>
              
              </div>
            </div>
            
            <p style={{
              margin: '2rem 0 1.5rem 0',
              fontSize: '0.95rem',
              lineHeight: '1.5',
              color: '#ccc',
              maxHeight: '6rem',
              overflow: 'auto'
            }}>
              {currentEpisode.episodeDescription}
            </p>
            
            {/* Rating UI */}
            <div style={{ position: 'absolute', bottom: '1rem', width: 'calc(100% - 4rem)' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: '#999'
              }}>
                {isVoted ? 'Your rating (ISDb):' : 'Rate (ISDb):'}
              </label>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                height: '3rem',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '0.5rem',
                padding: '0 0.25rem',
                marginBottom: '0.5rem'
              }}>
                {/* Numbers 1-10 with divider lines */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  position: 'relative',
                  padding: '0 0.25rem'
                }}>
                  {/* Divider lines between numbers */}
               
                  
                  {/* Numbers 1-10 */}
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <div
                      key={num}
                      onClick={() => handleRatingChange(currentEpisode.id, num)}
                      style={{
                        width: '2.2rem',
                        height: '2.2rem',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: '0.25rem',
                        backgroundColor: currentRating === num ? '#6600CC' : 'transparent',
                        color: currentRating === num ? 'white' : '#888',
                        fontWeight: currentRating === num ? 'bold' : 'normal',
                        transition: 'all 0.2s ease',
                        zIndex: 2
                      }}
                    >
                      {num}
                      {/* Only show divider if not the first number */}
                      {num > 1 && (
                        <div style={{
                          position: 'absolute', 
                          left: '-.2rem', 
                          height: '2rem', 
                          width: '2px', 
                          background: 'rgba(255, 255, 255, 0.1)'
                        }}/>
                      )}
                    </div>
                  ))}
                </div>
                
              
        
              </div>
            
            </div>
            </div>
        
          </div>

          
          <button
            onClick={nextEpisode}
            disabled={submitting || currentEpisodeIndex === episodes.length - 1}
            style={{
              padding: '0.5rem',
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              cursor: (submitting || currentEpisodeIndex === episodes.length - 1) ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'auto',
              alignSelf: 'stretch',
              flexShrink: 0
            }}
          >
            {submitting ? <span class="loader"></span>: <ChevronRight size={48} />}
          </button>
        </div>
      </div>
    );
  }
  
  // Return empty state if no episodes
  return (
    <div style={{ 
      padding: '2rem',
      backgroundColor: '#1a1a1a',
      borderRadius: '0.5rem',
      textAlign: 'center',
      background: `
        radial-gradient(circle at top left, rgba(102, 0, 204, 0.4) 0%, rgba(0, 0, 0, 0) 30%),
        radial-gradient(circle at bottom right, rgba(102, 0, 204, 0.3) 0%, rgba(0, 0, 0, 0) 40%),
        radial-gradient(circle at top right, rgba(119, 0, 238, 0.2) 0%, rgba(0, 0, 0, 0) 35%),
        linear-gradient(135deg, rgba(26, 26, 26, 1) 0%, rgba(18, 18, 18, 1) 100%)
      `,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 2px rgba(102, 0, 204, 0.3) inset'
    }}>
      <p>No recent episodes from your friends.</p>
      <button
        onClick={() => navigate('/add-friend')}
        style={{
          marginTop: '1rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: '#6600CC',
          color: 'white',
          border: 'none',
          borderRadius: '0.5rem',
          fontSize: '0.9rem',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <RefreshCw size={16} />
        Refresh Feed
      </button>
    </div>
  );
};

export default FeedDesktop; 